import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { useZh, useOpenSignup } from '../lib/useProduct';
import type { UserProfile } from '../lib/types';

type LandingSession = { email: string | null };

// The landing page is a single punchline and one action (user direction,
// 2026-08-13): auth entries live in the header's top-right, the centre CTA is
// 先逛职位板 — the board sells the product better than copy about it. The
// daily digest module that used to render below the hero is gone from here
// for the same reason; /news remains its home, and the copy-for-posting
// workflow lives there.

export default function Landing() {
  const zh = useZh();
  // Anonymous visitors see this page and the public board (/board, served by
  // Shell's PublicShell branch) — any surface offering signup must read the
  // flag. This one didn't at first: with signup closed it promised "免费注册"
  // while /api/register answered invite_required, and the whole public funnel
  // dead-ended on a broken promise.
  const openSignup = useOpenSignup();
  // Anonymous visitors used to be bounced straight to /login, which meant the
  // product had no public front door at all — you had to already have an
  // account to find out what it does. Auth state now comes from /api/session
  // (public, never 401s), so this page can market to logged-out visitors.
  const sessionQ = useQuery({
    queryKey: ['session'],
    queryFn: () => apiGet<LandingSession>('/api/session'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const signedIn = !!sessionQ.data?.email;

  // Only ask for the profile once we know there's a session — /api/profile
  // 401s for anonymous callers, and api.ts turns any 401 into a hard redirect
  // to /login, which would undo the whole point of the change above.
  const profileQ = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet<UserProfile>('/api/profile'),
    retry: false,
    staleTime: 30 * 1000,
    enabled: signedIn,
  });

  if (sessionQ.isLoading || (signedIn && profileQ.isLoading)) {
    // Render a quiet blank for the brief moment before the queries resolve,
    // so returning tenants don't get a flash of the welcome page.
    return <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />;
  }
  if (signedIn && profileQ.data && profileQ.data.onboarded) {
    return <Navigate to="/matches" replace />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '22px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--ink)', color: '#fff', display: 'grid', placeItems: 'center', fontFamily: 'Fraunces, var(--cjk-serif), serif', fontWeight: 700, fontSize: 14 }}>N</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Nice2Job</div>
        </div>
        {/* Both auth entries live here, top-right — the hero below carries no
            register/login buttons at all (user direction). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {signedIn ? (
            <Link to="/profile" className="btn accent">{zh ? '开始 →' : 'Get started →'}</Link>
          ) : (
            <>
              <Link to="/login" style={{ fontSize: 13.5, color: 'var(--ink2)' }}>
                {zh ? '登录' : 'Sign in'}
              </Link>
              {openSignup && (
                <Link to="/register" className="btn accent">
                  {zh ? '免费注册 →' : 'Sign up free →'}
                </Link>
              )}
            </>
          )}
        </div>
      </header>

      <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '72px 24px 56px' }}>
        <div style={{ maxWidth: 760, textAlign: 'center' }} className="page-enter">
          <h1 className="serif" style={{ fontSize: 68, lineHeight: 1.08, letterSpacing: '-0.03em', margin: 0, fontWeight: 400 }}>
            {zh
              ? <>为每个英国华人，<br /><em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>找到一份工作或实习。</em></>
              : <>Find the roles <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>that deserve you.</em></>}
          </h1>
          <p style={{ fontSize: 17, color: 'var(--ink2)', lineHeight: 1.6, margin: '26px auto 38px', maxWidth: 520 }}>
            {zh
              ? '英国实习和毕业生岗位，每天自动更新。注册后，用中文写经历，AI 帮你写成英文简历。'
              : 'UK internship and graduate roles, refreshed daily. Sign up and the AI turns your story into a CV employers read.'}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            {signedIn ? (
              <>
                <Link to="/profile" className="btn accent lg">{zh ? '完善我的资料' : 'Set up my profile'}</Link>
                {zh && <Link to="/board" className="btn lg">先逛职位板</Link>}
              </>
            ) : (
              <Link to="/board" className="btn accent lg">{zh ? '先逛职位板 →' : 'Browse the board →'}</Link>
            )}
          </div>
          {!signedIn && !openSignup && (
            <p style={{ fontSize: 12.5, color: 'var(--ink3)', marginTop: 20, lineHeight: 1.6 }}>
              {zh
                ? '目前是邀请制内测，注册需要邀请链接。'
                : 'Currently an invite-only beta — you need an invite link to sign up.'}
            </p>
          )}
          {zh && (
            <p style={{ fontSize: 12.5, color: 'var(--ink3)', marginTop: 26, lineHeight: 1.6 }}>
              我们只生成申请材料，绝不替你投递 —— 每一份都由你自己确认后提交。
            </p>
          )}
        </div>
      </div>

      <footer style={{ padding: '20px 48px', borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink3)', textAlign: 'center' }}>
        {zh
          ? '你的简历和申请记录只属于你'
          : 'Local. Private. Yours. · Runs on Ollama + a local Node server.'}
        {' · '}
        <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>{zh ? '隐私政策' : 'Privacy'}</Link>
        {' · '}
        <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>{zh ? '服务条款' : 'Terms'}</Link>
      </footer>
    </div>
  );
}
